import React, { useState, useEffect } from "react";
import { AiFillGithub } from "react-icons/ai";
import { BsArrowUpRightSquareFill } from "react-icons/bs";
import { motion, AnimatePresence } from "framer-motion";
import { getProjectsList } from "../data/data.js";

const categories = ["All", "Frontend & Web", "SaaS App", "Full Stack"];

const Work = () => {
  const [activeCategory, setActiveCategory] = useState("All");
  const [projectsList, setProjectsList] = useState(getProjectsList());

  useEffect(() => {
    const handleStorage = () => {
      setProjectsList(getProjectsList());
    };
    window.addEventListener("storage", handleStorage);
    window.addEventListener("projectsUpdated", handleStorage);
    return () => {
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener("projectsUpdated", handleStorage);
    };
  }, []);

  const filterProjects = (category) => {
    if (category === "All") return projectsList;
    return projectsList.filter((item) => {
      if (item.category && item.category === category) return true;
      if (category === "Frontend & Web") {
        return (
          (item.category && (item.category === "Frontend & Web" || item.category === "E-Commerce")) ||
          item.name.toLowerCase().includes("portfolio") ||
          item.name.toLowerCase().includes("flashcard") ||
          item.name.toLowerCase().includes("school") ||
          item.name.toLowerCase().includes("dailymotion") ||
          item.name.toLowerCase().includes("aahan") ||
          item.name.toLowerCase().includes("survey") ||
          item.name.toLowerCase().includes("presto") ||
          item.name.toLowerCase().includes("polstar") ||
          item.name.toLowerCase().includes("ca book") ||
          item.name.toLowerCase().includes("facility") ||
          item.name.toLowerCase().includes("kolkata") ||
          item.name.toLowerCase().includes("mbb") ||
          item.name.toLowerCase().includes("bank") ||
          item.name.toLowerCase().includes("shopsy") ||
          item.name.toLowerCase().includes("falvoro") ||
          item.name.toLowerCase().includes("e_commerce") ||
          item.name.toLowerCase().includes("e-commerce")
        );
      }
      if (category === "SaaS App") {
        return (
          ((item.category && item.category.toLowerCase().includes("saas")) ||
            item.name.toLowerCase().includes("saas") ||
            (item.intro && item.intro.toLowerCase().includes("saas"))) &&
          item.id !== 15 &&
          item.live !== "https://mybankbook.in/"
        );
      }
      if (category === "Full Stack" || category === "Full Stack MERN") {
        return (
          (item.category === "Full Stack MERN" ||
            item.category === "Full Stack" ||
            (item.technology && (item.technology.includes("MongoDB") || item.technology.includes("MongoDb")))) &&
          item.id !== 15 &&
          item.id !== 16 &&
          item.live !== "https://mybankbook.in/" &&
          item.live !== "https://www.wbicabooks.com/"
        );
      }
      return false;
    });
  };

  const filteredData = filterProjects(activeCategory);

  return (
    <section
      id="work"
      name="work"
      className="relative w-full min-h-screen bg-[#0F172A] py-24 text-slate-100 overflow-hidden"
    >
      {/* Background Orbs */}
      <div className="absolute top-1/4 left-1/3 w-[32rem] h-[32rem] bg-blue-600/10 rounded-full blur-[140px] pointer-events-none" />
      <div className="absolute bottom-1/4 right-1/4 w-[30rem] h-[30rem] bg-[#00B4DB]/12 rounded-full blur-[140px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <span className="text-[#00B4DB] text-xs font-semibold uppercase tracking-widest px-3 py-1 rounded-full bg-[#00B4DB]/10 border border-[#00B4DB]/30">
            Featured Portfolio
          </span>
          <h2 className="text-3xl sm:text-5xl font-extrabold text-white mt-4 tracking-tight">
            Selected <span className="gradient-text">Projects</span>
          </h2>
          <p className="text-slate-400 text-sm sm:text-base mt-3">
            A showcase of web applications, SaaS dashboards, and interactive user interfaces.
          </p>
        </div>

        {/* Filter Navigation Bar */}
        <div className="flex justify-center mb-14 px-2">
          <div className="inline-flex flex-wrap justify-center gap-1.5 sm:gap-2 p-1.5 sm:p-2 bg-[#1E293B]/80 backdrop-blur-xl border border-slate-700/80 rounded-2xl shadow-xl shadow-slate-950/40 z-10 max-w-full">
            {categories.map((cat) => {
              const isActive = activeCategory === cat;
              return (
                <motion.button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  whileTap={{ scale: 0.96 }}
                  className="relative px-4 sm:px-6 py-2.5 rounded-xl text-xs sm:text-sm font-semibold transition-colors duration-200 flex items-center justify-center select-none group"
                >
                  {isActive && (
                    <motion.div
                      layoutId="activeWorkTab"
                      className="absolute inset-0 bg-gradient-to-r from-blue-600 via-sky-600 to-[#00B4DB] rounded-xl shadow-md shadow-blue-500/25"
                      transition={{ type: "spring", stiffness: 450, damping: 35 }}
                    />
                  )}
                  <span
                    className={`relative z-10 transition-colors duration-200 ${
                      isActive ? "text-white font-bold" : "text-slate-300 hover:text-white"
                    }`}
                  >
                    {cat}
                  </span>
                </motion.button>
              );
            })}
          </div>
        </div>

        {/* Card Flex Container (Centered Cards with Smooth Tab Transitions) */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeCategory}
            initial={{ opacity: 0, y: 15, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -15, scale: 0.98 }}
            transition={{ duration: 0.22, ease: "easeOut" }}
            className="flex flex-wrap justify-center gap-8 max-w-7xl mx-auto min-h-[360px]"
          >
            {filteredData.map((item, index) => (
              <motion.div
                initial={{ opacity: 0, y: 20, scale: 0.94 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{
                  duration: 0.25,
                  delay: Math.min(index * 0.035, 0.3),
                  ease: "easeOut",
                }}
                whileHover={{ y: -6 }}
                key={item.id || item.name}
                className="group w-full sm:w-[340px] md:w-[350px] lg:w-[360px] bg-[#1E293B]/85 border border-slate-700/80 rounded-2xl overflow-hidden shadow-xl flex flex-col hover:border-[#00B4DB]/60 hover:shadow-2xl transition-all duration-300"
              >
                {/* Image & Overlay */}
                <div className="relative h-48 overflow-hidden bg-slate-900">
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-[#0F172A]/85 opacity-0 group-hover:opacity-100 backdrop-blur-sm transition-all duration-300 flex items-center justify-center gap-4">
                    {item.github && (
                      <a
                        href={item.github}
                        target="_blank"
                        rel="noreferrer"
                        className="p-3 bg-[#0F172A] border border-slate-700 rounded-full text-slate-200 hover:text-[#00B4DB] hover:border-[#00B4DB] hover:scale-110 transition-all shadow-md"
                        title="View GitHub Code"
                      >
                        <AiFillGithub size={24} />
                      </a>
                    )}
                    {item.live && (
                      <a
                        href={item.live}
                        target="_blank"
                        rel="noreferrer"
                        className="p-3 bg-gradient-to-r from-blue-600 via-sky-600 to-[#00B4DB] rounded-full text-white hover:scale-110 transition-all font-bold shadow-md shadow-blue-500/25"
                        title="View Live Demo"
                      >
                        <BsArrowUpRightSquareFill size={24} />
                      </a>
                    )}
                  </div>
                </div>

                {/* Content */}
                <div className="p-6 flex flex-col flex-1 justify-between">
                  <div>
                    {/* Category Pill Tag (Dedicated Row) */}
                    {item.category && (
                      <div className="w-full flex items-center mb-2.5">
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider text-[#00B4DB] bg-[#00B4DB]/10 border border-[#00B4DB]/30">
                          <span className="w-1.5 h-1.5 rounded-full bg-[#00B4DB]" />
                          {item.category}
                        </span>
                      </div>
                    )}

                    {/* Card Title */}
                    <div className="w-full">
                      <h3 className="text-lg sm:text-xl font-extrabold text-white tracking-tight leading-snug group-hover:text-[#00B4DB] transition-colors duration-200 line-clamp-2">
                        {item.name}
                      </h3>
                      <span className="block h-[2px] w-0 group-hover:w-12 transition-all duration-300 bg-gradient-to-r from-blue-600 to-[#00B4DB] rounded-full mt-1" />
                    </div>

                    {/* Content Description */}
                    <p className="text-slate-300 font-normal text-xs sm:text-sm mt-2 leading-relaxed line-clamp-4">
                      {item.intro}
                    </p>
                  </div>

                  {/* Tech Tags */}
                  {item.technology && (
                    <div className="mt-6 pt-4 border-t border-slate-700/80 flex flex-wrap gap-1.5">
                      {item.technology.split(",").map((t, i) => (
                        <span
                          key={i}
                          className="px-2.5 py-1 text-[11px] font-medium text-[#00B4DB] bg-[#00B4DB]/10 rounded-md border border-[#00B4DB]/20"
                        >
                          {t.trim()}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};

export default Work;
